import HttpClient from "../../utils/http";
import {
  cms_amiforyou_endpoint,
  cms_app_polling_endpoint,
  cms_banner_endpoint,
  cms_media_endpoint,
  cms_news_endpoint,
  cms_news_source_endpoint,
} from "../constants/api_endpoints";

export interface CmsListFilters {
  page: number;
  size: number;
  search?: string;
  status?: string;
  orgId?: number | null;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
  // yyyy-mm-dd — only applied server-side once both are given.
  fromDate?: string;
  toDate?: string;
  // Media only — a broad family (image/video/audio/other), not an exact
  // mime_type.
  mediaType?: string;
  // Banners only.
  bannerType?: string | null;
}

// The backend responds with a 404 "Data Not Found" error when a list query
// matches zero rows instead of returning an empty list, so list calls need to
// normalize that into a real empty-list response for the UI's empty state.
const EMPTY_LIST_RESPONSE = { data: { data: { total: 0, list: [] } } };

async function fetchListWithEmptyFallback(url: string) {
  try {
    return await HttpClient.get(url);
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return EMPTY_LIST_RESPONSE;
    }
    throw error;
  }
}

function buildListQuery(filters: CmsListFilters): string {
  const params = new URLSearchParams({
    page: filters.page.toString(),
    size: filters.size.toString(),
  });
  if (filters.search) params.append("search", filters.search);
  if (filters.status) params.append("status", filters.status);
  if (filters.orgId) params.append("orgId", filters.orgId.toString());
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
  if (filters.fromDate) params.append("from_date", filters.fromDate);
  if (filters.toDate) params.append("to_date", filters.toDate);
  if (filters.mediaType) params.append("media_type", filters.mediaType);
  if (filters.bannerType) params.append("bannerType", filters.bannerType);
  return params.toString();
}

// News
export const getNewsList = (filters: CmsListFilters) =>
  fetchListWithEmptyFallback(`${cms_news_endpoint}?${buildListQuery(filters)}`);

// News is always created in English; other languages are added afterwards
// via updateNews with a `locale` header, so getNewsById/updateNews take a
// locale (defaulting to English) while createNews does not.
export const getNewsById = (id: number | string, locale: string = "en") =>
  HttpClient.get(`${cms_news_endpoint}/${id}`, { headers: { locale } });

export const createNews = (payload: any) =>
  HttpClient.post(cms_news_endpoint, payload);

export const updateNews = (id: number | string, locale: string, payload: any) =>
  HttpClient.put(`${cms_news_endpoint}/${id}`, payload, { headers: { locale } });

export const deleteNews = (id: number | string) =>
  HttpClient.delete(`${cms_news_endpoint}/${id}`);

export function downloadNewsBulkUploadTemplate() {
  return HttpClient.get(`${cms_news_endpoint}/bulk-upload/template`, {
    responseType: "blob",
  });
}

export function bulkUploadNews(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return HttpClient.post(`${cms_news_endpoint}/bulk-upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export function downloadNewsTranslationsBulkUploadTemplate() {
  return HttpClient.get(`${cms_news_endpoint}/bulk-upload/translations/template`, {
    responseType: "blob",
  });
}

export function bulkUploadNewsTranslations(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return HttpClient.post(`${cms_news_endpoint}/bulk-upload/translations`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

// News Source (channel) — the dropdown in the news form only needs active
// channels; org scoping/other filters mirror the other CMS list filters.
export interface NewsSourceListFilters {
  page?: number;
  size?: number;
  search?: string;
  isActive?: boolean;
  orgId?: number | null;
  // The channel `name` comes back localized to this (falling back to
  // English) - defaults to English when omitted.
  locale?: string;
}

export const getNewsSourceList = (filters: NewsSourceListFilters = {}) => {
  const params = new URLSearchParams();
  params.append("page", String(filters.page ?? 1));
  params.append("size", String(filters.size ?? 100));
  if (filters.search) params.append("search", filters.search);
  if (filters.isActive !== undefined) params.append("isActive", String(filters.isActive));
  if (filters.orgId) params.append("orgId", String(filters.orgId));
  return HttpClient.get(`${cms_news_source_endpoint}?${params.toString()}`, {
    headers: { locale: filters.locale || "en" },
  });
};

// Banner
export const getBannerList = (filters: CmsListFilters) =>
  fetchListWithEmptyFallback(`${cms_banner_endpoint}?${buildListQuery(filters)}`);

export const getBannerById = (id: number | string) =>
  HttpClient.get(`${cms_banner_endpoint}/${id}`);

export const createBanner = (payload: any) =>
  HttpClient.post(cms_banner_endpoint, payload);

export const updateBanner = (id: number | string, payload: any) =>
  HttpClient.put(`${cms_banner_endpoint}/${id}`, payload);

export const deleteBanner = (id: number | string) =>
  HttpClient.delete(`${cms_banner_endpoint}/${id}`);

// Media
export const getMediaList = (filters: CmsListFilters) =>
  fetchListWithEmptyFallback(`${cms_media_endpoint}?${buildListQuery(filters)}`);

// No max-size limit is enforced anywhere on the backend (admin_cms_service
// or shared) for CMS media, so this is a client-side-only guard to keep
// uploads reasonable and give immediate feedback instead of a slow failure.
export const MAX_MEDIA_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
export const MAX_MEDIA_FILE_SIZE_LABEL = "5 MB";

export const getMediaById = (id: number | string) =>
  HttpClient.get(`${cms_media_endpoint}/${id}`);

// Callers display errors via `e?.response?.data?.error?.message`, matching
// the shape of a failed HttpClient call. Shaping plain errors the same way
// means the real reason shows up instead of falling back to a generic
// "Something went wrong" message.
function buildUploadError(message: string) {
  const error: any = new Error(message);
  error.response = { data: { error: { message } } };
  return error;
}

function getImageDimensions(file: File): Promise<{ width?: number; height?: number }> {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(objectUrl);
    };
    img.onerror = () => {
      resolve({});
      URL.revokeObjectURL(objectUrl);
    };
    img.src = objectUrl;
  });
}

// `new Image()` can't read a video's frame size, so video files need their
// own element to read `videoWidth`/`videoHeight` off loaded metadata.
function getVideoDimensions(file: File): Promise<{ width?: number; height?: number }> {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      resolve({ width: video.videoWidth, height: video.videoHeight });
      URL.revokeObjectURL(objectUrl);
    };
    video.onerror = () => {
      resolve({});
      URL.revokeObjectURL(objectUrl);
    };
    video.src = objectUrl;
  });
}

// Media upload is now a 3-step signed-URL flow (admin_cms_service commit
// "signed url added"): get a signed GCS write URL, PUT the file to it
// directly, then confirm the upload so the backend creates the DB row.
// Width/height are no longer computed server-side, so we measure them here.
export const uploadMedia = async (file: File, title?: string, altText?: string) => {
  if (file.size > MAX_MEDIA_FILE_SIZE_BYTES) {
    throw buildUploadError(`File is too large. Maximum allowed size is ${MAX_MEDIA_FILE_SIZE_LABEL}.`);
  }

  const contentType = file.type || "image/jpeg";

  const signedUrlResponse = await HttpClient.post(`${cms_media_endpoint}/signed-url`, {
    fileName: file.name,
    contentType,
  });

  const { signed_url, file_path } = signedUrlResponse?.data?.data || {};
  if (!signed_url || !file_path) {
    throw buildUploadError("Failed to get signed URL for media upload");
  }

  const fileBlob = await file.arrayBuffer();
  let uploadResponse: Response;
  try {
    uploadResponse = await fetch(signed_url, {
      method: "PUT",
      body: fileBlob,
      headers: { "Content-Type": contentType },
    });
  } catch (error) {
    // A network-level fetch failure here (rather than a non-ok HTTP status)
    // almost always means the browser blocked the cross-origin PUT to GCS -
    // most likely the bucket's CORS config doesn't allow PUT/this origin yet.
    throw buildUploadError(
      "Could not reach storage to upload the file. This usually means the GCS bucket's CORS policy needs to allow PUT requests from this app's origin."
    );
  }
  if (!uploadResponse.ok) {
    const bodyText = await uploadResponse.text().catch(() => "");
    throw buildUploadError(
      `Storage rejected the upload (HTTP ${uploadResponse.status}).${bodyText ? ` ${bodyText}` : ""}`
    );
  }

  const { width, height } = contentType.startsWith("video/")
    ? await getVideoDimensions(file)
    : await getImageDimensions(file);

  return HttpClient.post(cms_media_endpoint, {
    file_path,
    title: title || file.name,
    alt_text: altText || undefined,
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
  });
};

export const deleteMedia = (id: number | string) =>
  HttpClient.delete(`${cms_media_endpoint}/${id}`);

// AMI For You
export interface AmiForYouListFilters {
  page: number;
  size: number;
  search?: string;
  // Unlike News/Banner's draft/published string status, AMI For You's status
  // is a plain boolean flag - undefined means "no filter" (both).
  status?: boolean;
  orgId?: number | null;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
  fromDate?: string;
  toDate?: string;
}

function buildAmiForYouListQuery(filters: AmiForYouListFilters): string {
  const params = new URLSearchParams({
    page: filters.page.toString(),
    size: filters.size.toString(),
  });
  if (filters.search) params.append("search", filters.search);
  if (filters.status !== undefined) params.append("status", String(filters.status));
  if (filters.orgId) params.append("orgId", filters.orgId.toString());
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
  if (filters.fromDate) params.append("from_date", filters.fromDate);
  if (filters.toDate) params.append("to_date", filters.toDate);
  return params.toString();
}

export const getAmiForYouList = (filters: AmiForYouListFilters) =>
  fetchListWithEmptyFallback(`${cms_amiforyou_endpoint}?${buildAmiForYouListQuery(filters)}`);

// AMI For You is always created in English; other languages are added
// afterwards via updateAmiForYou with a `locale` header, same convention as
// News.
export const getAmiForYouById = (id: number | string, locale: string = "en") =>
  HttpClient.get(`${cms_amiforyou_endpoint}/${id}`, { headers: { locale } });

export const createAmiForYou = (payload: any) =>
  HttpClient.post(cms_amiforyou_endpoint, payload);

export const updateAmiForYou = (id: number | string, locale: string, payload: any) =>
  HttpClient.put(`${cms_amiforyou_endpoint}/${id}`, payload, { headers: { locale } });

export const deleteAmiForYou = (id: number | string) =>
  HttpClient.delete(`${cms_amiforyou_endpoint}/${id}`);

// App Polling
export interface AppPollingListFilters {
  page: number;
  size: number;
  search?: string;
  // Plain boolean flag, same convention as AMI For You's status - undefined
  // means "no filter" (both).
  status?: boolean;
  orgId?: number | null;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
  fromDate?: string;
  toDate?: string;
}

function buildAppPollingListQuery(filters: AppPollingListFilters): string {
  const params = new URLSearchParams({
    page: filters.page.toString(),
    size: filters.size.toString(),
  });
  if (filters.search) params.append("search", filters.search);
  if (filters.status !== undefined) params.append("status", String(filters.status));
  if (filters.orgId) params.append("orgId", filters.orgId.toString());
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
  if (filters.fromDate) params.append("from_date", filters.fromDate);
  if (filters.toDate) params.append("to_date", filters.toDate);
  return params.toString();
}

export const getAppPollingList = (filters: AppPollingListFilters) =>
  fetchListWithEmptyFallback(`${cms_app_polling_endpoint}?${buildAppPollingListQuery(filters)}`);

// Always created in English; other languages are added afterwards via
// updateAppPolling with a `locale` header, same convention as AMI For You.
// Also used by the read-only details/results screen - the response always
// includes each option's vote_count/vote_percentage and total_responses.
export const getAppPollingById = (id: number | string, locale: string = "en") =>
  HttpClient.get(`${cms_app_polling_endpoint}/${id}`, { headers: { locale } });

export const createAppPolling = (payload: any) =>
  HttpClient.post(cms_app_polling_endpoint, payload);

export const updateAppPolling = (id: number | string, locale: string, payload: any) =>
  HttpClient.put(`${cms_app_polling_endpoint}/${id}`, payload, { headers: { locale } });

export const deleteAppPolling = (id: number | string) =>
  HttpClient.delete(`${cms_app_polling_endpoint}/${id}`);
